import { supabase } from '../lib/supabase';
import { PAYSTACK, PRICING, PLAN_DURATION } from '../lib/constants';

/**
 * Initialize Paystack payment
 */
export const initializePayment = async (user, planType = 'WEEKLY') => {
  try {
    const amount = PRICING[planType];
    
    const paymentData = {
      email: user.email,
      amount: amount * 100, // Convert to kobo (₦1,000 = 100,000 kobo)
      currency: PAYSTACK.CURRENCY,
      metadata: {
        user_id: user.id,
        plan_type: planType,
        full_name: user.user_metadata?.full_name || user.email,
        duration_days: PLAN_DURATION[planType],
      },
      channels: PAYSTACK.CHANNELS,
    };

    return paymentData;
  } catch (error) {
    console.error('Error initializing payment:', error);
    throw error;
  }
};

/**
 * ✅ SECURE: Verify via Supabase Edge Function
 */
export const verifyPayment = async (reference) => {
  try {
    const { data, error } = await supabase.functions.invoke('verify-paystack-payment', {
      body: { reference }
    });

    if (error) throw error;

    // data.status is the boolean from Paystack's API response
    if (data && data.status === true && data.data.status === 'success') {
      return { success: true, data: data.data };
    }

    return { success: false, message: 'Payment not yet confirmed' };
  } catch (error) {
    console.error('Verification Error:', error);
    return { success: false, message: error.message };
  }
};

/**
 * Calculate subscription end date
 */
export const calculateEndDate = (planType) => {
  const now = new Date();
  const durationDays = PLAN_DURATION[planType];
  const endDate = new Date(now.getTime() + durationDays * 24 * 60 * 60 * 1000);
  return endDate.toISOString();
};

/**
 * Update user subscription after successful payment
 */
export const updateSubscription = async (userId, paymentData) => {
  try {
    const planType = paymentData.metadata.plan_type;
    const endDate = calculateEndDate(planType);

    // Update profile to premium
    const { error: profileError } = await supabase
      .from('profiles')
      .update({ 
        subscription_plan: 'premium',
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId);

    if (profileError) throw profileError;

    const { error: subError } = await supabase
  .from('subscriptions')
  .upsert({
    user_id: userId,
    plan_type: planType.toUpperCase(), // Standardize to uppercase
    status: 'active',
    start_date: new Date().toISOString(),
    end_date: endDate,
    payment_reference: paymentData.reference,
    payment_provider: 'paystack',
    amount_paid: paymentData.amount / 100,
    currency: paymentData.currency || 'NGN',
    auto_renew: false,
  }, { onConflict: 'user_id' }); 
    if (subError) throw subError;

    return { success: true, endDate };
  } catch (error) {
    console.error('Error updating subscription:', error);
    throw error;
  }
};

/**
 * Get active subscription details
 */
export const getActiveSubscription = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') throw error;

    return data;
  } catch (error) {
    console.error('Error getting subscription:', error);
    return null;
  }
};

/**
 * Check if subscription has expired and update if needed
 */
export const checkAndUpdateExpiredSubscription = async (userId) => {
  try {
    const subscription = await getActiveSubscription(userId);
    
    if (!subscription) return { expired: false };

    const now = new Date();
    const endDate = new Date(subscription.end_date);

    if (now > endDate) {
      // Subscription expired, downgrade user
      await supabase
        .from('profiles')
        .update({ subscription_plan: 'free' })
        .eq('id', userId);

      await supabase
        .from('subscriptions')
        .update({ status: 'expired' })
        .eq('id', subscription.id);

      return { expired: true };
    }

    return { expired: false, subscription };
  } catch (error) {
    console.error('Error checking subscription:', error);
    return { expired: false };
  }
};